import { validationConstants } from "constants/formValidation";
import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { distributionLogicOptions } from "constants/paymentPlanConstants";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parseDate } from "helpers/date";
import { addClearPatientFee, removeClearPatientFee } from "helpers/fee";
import { setAmountInstallmentCount } from "helpers/paymentPlanInstallments";
import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { handleChangeDateWithMask } from "pages/CreateOrder/utils/mask";
import { Dropdown } from "primereact/dropdown";
import {
  InputNumber,
  InputNumberValueChangeParams,
} from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { useMemo, useCallback, useEffect, useState, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";
import { IPaymentPlan } from "types/Payment";

import { ReadOnlyLabel, ReadOnlyValue } from "../../SectionParts";
import {
  validateNextInstallmentDate,
  validateMinDownPaymentFlat,
} from "../helpers/helper";
import { IPaymentDetailsEdit } from "../PaymentDetails";
import { PaymentPlanDetails } from "./PaymentPlanDetails";
import { RemainingAmountEditTable } from "./RemainingAmountEditTable";

const INSTALLMENT_FREQUENCY_OPTIONS = [
  { label: "Monthly", value: "Monthly" },
  { label: "Weekly", value: "Weekly" },
  { label: "BiWeekly", value: "Biweekly" },
];

const MIN_DOWN_PAYMENT_TYPE_OPTIONS = [
  { label: "%", value: "Percentage" },
  { label: "$", value: "Flat amount" },
];

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  input {
    width: 100%;
  }
`;

export function PaymentPlanSection({
  editing,
  editable,
  orderStatus,
  totalPatientPortion,
  order,
  orderType,
  paymentPlanBreakDown,
  paymentPlan,
  setPaymentPlanFieldsValid,
  showPaymentPlanEnableButton,
  paymentPlanRemaining,
  differenceByProvider,
  setDifferenceByProvider,
  valuesByProvider,
  setValuesByProvider,
  initialRemainingInstallmentsCount,
  showMinPaymentWarning,
  setShowMinPaymentWarning,
  selectedPaymentOption,
}: {
  editing: boolean;
  editable: boolean;
  orderStatus: string;
  totalPatientPortion: number | null;
  order?: IOrder;
  orderType: string;
  paymentPlanBreakDown?: any;
  paymentPlan?: IPaymentPlan;
  setPaymentPlanFieldsValid: (isValid: boolean) => void;
  showPaymentPlanEnableButton: boolean;
  paymentPlanRemaining?: Array<{
    providerId: string;
    remainingAmountInCents: number;
    providerPriority?: number | null;
  }>;
  differenceByProvider: { [key: string]: number };
  setDifferenceByProvider: (differenceByProvider: {
    [key: string]: number;
  }) => void;
  valuesByProvider: { [key: string]: number };
  setValuesByProvider: (valuesByProvider: { [key: string]: number }) => void;
  initialRemainingInstallmentsCount: number | null;
  showMinPaymentWarning: boolean;
  setShowMinPaymentWarning: (showMinPaymentWarning: boolean) => void;
  selectedPaymentOption: string;
}) {
  const {
    control,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<IPaymentDetailsEdit>();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const { userInfo } = useUserWithAccount();
  const minDownPaymentType = watch("paymentPlanMinDownPaymentType");
  const minDownPaymenFlat = watch("paymentPlanMinDownPaymentFlat");
  const remainingInstallmentsField = watch("paymentPlanDuration");
  const remainingAmountField = watch("paymentPlanRemainingAmount");
  const nextInstallmentDateField = watch("nextInstallmentDate");
  const minDownPaymentPercent = watch("paymentPlanMinDownPaymentPercent");
  const installmentAmountField = watch("installmentAmount");
  const installmentFrequencyField = watch("installmentFrequency");

  // Maximum allowed payment plan duration (for min installment calculation)
  const MAX_PAYMENT_PLAN_DURATION = 50;

  // Initial payment (down payment) in cents for min/max installment calculation
  const initialPayment = useMemo(() => {
    if (minDownPaymentType === "Flat amount") {
      return dollarsToCents(minDownPaymenFlat || 0);
    }
    return (totalPatientPortion || 0) * ((minDownPaymentPercent || 0) / 100);
  }, [totalPatientPortion, minDownPaymentPercent, minDownPaymenFlat]);

  // Remaining amount in cents (amount to be split into installments)
  const remainingAmountInCents = useMemo(() => {
    if (remainingAmountField != null && remainingAmountField !== undefined) {
      return dollarsToCents(remainingAmountField);
    }
    const total = totalPatientPortion ?? 0;
    return Math.max(0, total - initialPayment);
  }, [remainingAmountField, totalPatientPortion, initialPayment]);

  const maxInstallmentAmount = useMemo(() => {
    if (remainingAmountInCents <= 0) return 0;
    return centsToDollars(remainingAmountInCents);
  }, [remainingAmountInCents]);

  const minInstallmentAmount = useMemo(() => {
    if (remainingAmountInCents <= 0) return 0;
    return centsToDollars(
      Math.ceil(remainingAmountInCents / MAX_PAYMENT_PLAN_DURATION)
    );
  }, [remainingAmountInCents]);

  // State to persist the attention message
  const [persistentRecommendation, setPersistentRecommendation] = useState<
    string | null
  >(null);

  // Track the last remaining amount that triggered auto-adjustment
  const [lastAdjustedAmount, setLastAdjustedAmount] = useState<number | null>(
    null
  );

  // Track previous installments to detect manual changes
  const [prevInstallments, setPrevInstallments] = useState<number | null>(
    remainingInstallmentsField
  );

  // Helper function to calculate payment breakdown data
  const calculatePaymentBreakdown = useCallback(
    (
      paymentPlanBreakDown: any,
      hasClearPatientFee: boolean,
      paymentPlanRemaining?: any[]
    ) => {
      if (!paymentPlanBreakDown?.payments?.length) {
        return { providers: [], accurateTotal: null };
      }

      const paymentsWithoutInitialDeposit =
        paymentPlanBreakDown.payments.filter((p: any) => !!p.installmentInDb);
      const paymentsStillNotPaid = paymentsWithoutInitialDeposit.filter(
        (p: any) => !p.installmentInDb.paymentId
      );

      const providerMap = new Map();
      let totalWithClearPatientFee = 0;

      // Build the provider map from breakdown data to get metadata
      // Use ALL payments (including initial deposit) to ensure we have provider metadata
      paymentPlanBreakDown.payments.forEach((payment: any) => {
        payment.providerBreakdown.forEach((provider: any) => {
          const { providerId, providerName, providerPriority, cptCode } =
            provider;
          if (!providerMap.has(providerId)) {
            providerMap.set(providerId, {
              providerId,
              providerName,
              providerPriority,
              cptCode,
              totalExpectedTransferAmount: 0,
            });
          }
        });
      });

      // Calculate accurate total from unpaid payments only (for clear patient fee calculation)
      paymentsStillNotPaid.forEach((payment: any) => {
        payment.providerBreakdown.forEach((provider: any) => {
          // For accurate total calculation (only when clear patient fee exists)
          if (hasClearPatientFee) {
            totalWithClearPatientFee +=
              provider.expectedTransferAmount +
              provider.clearFeeAmountInCents +
              (provider.clearPatientFeeAmountInCents || 0);
          }
        });
      });

      // Set remaining amounts from backend data
      if (paymentPlanRemaining?.length) {
        paymentPlanRemaining.forEach((remaining) => {
          if (providerMap.has(remaining.providerId)) {
            providerMap.get(remaining.providerId).totalExpectedTransferAmount =
              remaining.remainingAmountInCents;
          }
        });
      }

      return {
        providers: Array.from(providerMap.values()),
        accurateTotal: hasClearPatientFee ? totalWithClearPatientFee : null,
      };
    },
    []
  );

  // Calculate both provider breakdown and accurate total in one helper function
  const paymentBreakdownResult = useMemo(() => {
    const hasClearPatientFee =
      order?.clearPatientFeeValueInPercentage &&
      order.clearPatientFeeValueInPercentage > 0;
    return calculatePaymentBreakdown(
      paymentPlanBreakDown,
      !!hasClearPatientFee,
      paymentPlanRemaining
    );
  }, [
    paymentPlanBreakDown,
    order?.clearPatientFeeValueInPercentage,
    calculatePaymentBreakdown,
    paymentPlanRemaining,
  ]);

  const remainingAmountBreakdown = paymentBreakdownResult.providers;
  const accurateRemainingTotal = paymentBreakdownResult.accurateTotal;

  const minDownPaymentEnabled = useMemo(() => {
    if (orderStatus !== orderStatusConstants.SENT_TO_PATIENT || !editing) {
      return false;
    }
    return true;
  }, [orderStatus, editing]);

  const showMinDownPaymentFields = useMemo(() => {
    if (!editing) {
      if (
        [
          orderStatusConstants.PARTIALLY_PAID,
          orderStatusConstants.PAID,
        ].includes(orderStatus)
      ) {
        return false;
      }
      return true;
    }
    return minDownPaymentEnabled;
  }, [minDownPaymentEnabled, editing]);

  const showPercentageField = useMemo(() => {
    if (!minDownPaymentType) {
      return false;
    }
    if (minDownPaymentType === "Percentage") {
      return true;
    }
    return false;
  }, [minDownPaymentType]);

  const showFlatAmountField = useMemo(() => {
    if (!minDownPaymentType) {
      return false;
    }
    if (minDownPaymentType === "Flat amount") {
      return true;
    }
    return false;
  }, [minDownPaymentType]);

  // Create validation function that uses the helper with current state
  const validateMinDownPaymentFlatWithState = useCallback(
    (value: number | null | undefined) => {
      return validateMinDownPaymentFlat(
        value,
        totalPatientPortion,
        order?.clearPatientFeeValueInPercentage,
        order?.status
      );
    },
    [totalPatientPortion, order?.clearPatientFeeValueInPercentage]
  );

  // Calculate display value for minimum down payment flat without clear patient fee, when it exists
  const minDownPaymentFlatDisplayValue = useMemo(() => {
    return minDownPaymenFlat &&
      order?.clearPatientFeeValueInPercentage &&
      orderType === orderTypeConstants.GFE
      ? removeClearPatientFee(
          minDownPaymenFlat,
          order.clearPatientFeeValueInPercentage
        )
      : minDownPaymenFlat;
  }, [minDownPaymenFlat, order?.clearPatientFeeValueInPercentage, orderType]);

  const showRemainingAmountField = useMemo(() => {
    if (
      [
        orderStatusConstants.SENT_TO_PATIENT,
        orderStatusConstants.EXPIRED,
        orderStatusConstants.NO_BALANCE_DUE,
      ].includes(orderStatus)
    ) {
      return false;
    }
    if (orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return true;
    }
    return !editing;
  }, [editing, orderStatus, orderType]);

  const remainingAmountDisabled = useMemo(() => {
    if (orderStatus === orderStatusConstants.SENT_TO_PATIENT) {
      return true;
    }
    return !editing;
  }, [orderStatus, editing]);

  const showNextInstallmentDate = useMemo(
    () =>
      orderStatus === orderStatusConstants.PARTIALLY_PAID ||
      (editing &&
        orderStatus === orderStatusConstants.PAID &&
        !!paymentPlanBreakDown?.payments?.length),
    [orderStatus, paymentPlan, editing, paymentPlanBreakDown]
  );

  const showRemainingAmountsSection = useMemo(() => {
    return (
      editing &&
      (orderStatus === orderStatusConstants.PARTIALLY_PAID ||
        orderStatus === orderStatusConstants.PAID) &&
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY
    );
  }, [editing, orderStatus, orderType]);

  const isPaymentPlanRemainingAmountChanged = useMemo(() => {
    return (
      dollarsToCents(remainingAmountField || 0) !==
      paymentPlan?.remainingAmountInCents
    );
  }, [remainingAmountField, paymentPlan?.remainingAmountInCents]);

  const rearrangeFieldsOrder = useMemo(() => {
    return orderStatus === orderStatusConstants.SENT_TO_PATIENT && editing;
  }, [orderStatus, editing]);

  // Previous monthly payment in cents from the initial payment plan.
  // When initial plan was "Set amount" (InstallmentAmount), use its installmentAmount;
  // otherwise use remaining amount / duration.
  const initialPreviousMonthlyPaymentInCents = useMemo(() => {
    if (
      paymentPlan?.calculationBasis === "InstallmentAmount" &&
      paymentPlan?.installmentAmountInCents != null &&
      paymentPlan.installmentAmountInCents > 0
    ) {
      return paymentPlan.installmentAmountInCents;
    }
    if (
      initialRemainingInstallmentsCount &&
      initialRemainingInstallmentsCount > 0 &&
      paymentPlan?.remainingAmountInCents != null
    ) {
      return Math.round(
        paymentPlan.remainingAmountInCents / initialRemainingInstallmentsCount
      );
    }
    return null;
  }, [
    paymentPlan?.calculationBasis,
    paymentPlan?.installmentAmountInCents,
    paymentPlan?.remainingAmountInCents,
    initialRemainingInstallmentsCount,
  ]);

  // Options passed when calling calculatePaymentInfo (setAmount vs payment plan).
  const paymentInfoOptions = useMemo(
    () => ({
      selectedPaymentOption,
      currentInstallmentAmountInCents:
        selectedPaymentOption === "setAmount" && installmentAmountField != null
          ? dollarsToCents(installmentAmountField)
          : null,
    }),
    [selectedPaymentOption, installmentAmountField]
  );

  // Calculation function for monthly payment and recommendations.
  // When setAmount: current monthly = selected installment amount (fixed), duration derived from amount/installment.
  // When payment plan: current monthly = amount / duration (duration is user-selected).
  const calculatePaymentInfo = (
    currentAmount: number,
    initialAmount: number,
    currentInstallments: number,
    hasChanges: boolean,
    initialPreviousMonthlyPaymentInCentsParam?: number | null,
    options?: {
      selectedPaymentOption?: string;
      currentInstallmentAmountInCents?: number | null;
    }
  ) => {
    const isSetAmount =
      options?.selectedPaymentOption === "setAmount" &&
      options?.currentInstallmentAmountInCents != null &&
      options.currentInstallmentAmountInCents > 0;

    if (isSetAmount && options?.currentInstallmentAmountInCents != null) {
      const installmentInCents = options.currentInstallmentAmountInCents;
      const currentMonthlyPayment = installmentInCents;
      const currentMonthlyText = parsePrice(
        centsToDollars(currentMonthlyPayment)
      );

      // Previous monthly: same logic as below (reuse helper)
      let previousMonthlyPaymentInCents: number | null = null;
      if (
        initialPreviousMonthlyPaymentInCentsParam !== undefined &&
        initialPreviousMonthlyPaymentInCentsParam !== null
      ) {
        previousMonthlyPaymentInCents =
          initialPreviousMonthlyPaymentInCentsParam;
      } else if (
        initialRemainingInstallmentsCount &&
        initialRemainingInstallmentsCount > 0
      ) {
        previousMonthlyPaymentInCents = Math.round(
          initialAmount / initialRemainingInstallmentsCount
        );
      }
      const hasValidPrevious =
        previousMonthlyPaymentInCents != null &&
        previousMonthlyPaymentInCents > 0;
      const monthlyPaymentInfo =
        hasChanges && hasValidPrevious
          ? `${currentMonthlyText} (previously ${parsePrice(
              centsToDollars(previousMonthlyPaymentInCents as number)
            )})`
          : currentMonthlyText;
      // No auto-adjust recommendation when setAmount (user fixed the amount; duration is derived)
      return {
        monthlyPaymentInfo,
        recommendationInfo: null,
        adjustedInstallments: null,
      };
    }

    // Payment plan option: require valid duration
    if (!currentInstallments || currentInstallments <= 0) {
      return {
        monthlyPaymentInfo: null,
        recommendationInfo: null,
        adjustedInstallments: null,
      };
    }

    const currentMonthlyPayment = currentAmount / currentInstallments;

    const currentMonthlyText = parsePrice(
      centsToDollars(currentMonthlyPayment)
    );

    // Previous monthly: use passed value, or derive from initial amount and installments when available
    let previousMonthlyPaymentInCents: number | null = null;
    if (
      initialPreviousMonthlyPaymentInCentsParam !== undefined &&
      initialPreviousMonthlyPaymentInCentsParam !== null
    ) {
      previousMonthlyPaymentInCents = initialPreviousMonthlyPaymentInCentsParam;
    } else if (
      initialRemainingInstallmentsCount &&
      initialRemainingInstallmentsCount > 0
    ) {
      previousMonthlyPaymentInCents = Math.round(
        initialAmount / initialRemainingInstallmentsCount
      );
    }

    const hasValidPrevious =
      previousMonthlyPaymentInCents != null &&
      previousMonthlyPaymentInCents > 0;

    // Only show "previously" if we have valid initial data
    const monthlyPaymentInfo =
      hasChanges && hasValidPrevious
        ? (() => {
            const previousMonthlyText = parsePrice(
              centsToDollars(previousMonthlyPaymentInCents as number)
            );
            return `${currentMonthlyText} (previously ${previousMonthlyText})`;
          })()
        : `${currentMonthlyText}`;

    // Calculate recommendation (hasValidPrevious ensures non-null here)
    let recommendationInfo = null;
    if (hasChanges && hasValidPrevious) {
      const initialPreviousMonthlyPayment =
        previousMonthlyPaymentInCents as number;

      // Calculate the best possible duration to get close to initial monthly payment
      const idealMonths = currentAmount / initialPreviousMonthlyPayment;
      let bestPossibleDurationCount = Math.max(1, Math.round(idealMonths));
      let recommendedMonthlyPayment = currentAmount / bestPossibleDurationCount;

      // Check if recommended monthly payment exceeds 6% above original
      const maxAllowedMonthlyPayment = initialPreviousMonthlyPayment * 1.06; // 6% above original
      if (recommendedMonthlyPayment > maxAllowedMonthlyPayment) {
        // Use the 2nd closest option (more months, lower payment)
        bestPossibleDurationCount += 1;
        recommendedMonthlyPayment = currentAmount / bestPossibleDurationCount;
      }

      bestPossibleDurationCount = Math.min(
        bestPossibleDurationCount,
        MAX_PAYMENT_PLAN_DURATION
      );

      // Check if the difference is significant (more than 7 cents)
      const monthlyPaymentDifference = Math.abs(
        currentMonthlyPayment - initialPreviousMonthlyPayment
      );
      const recommendedDifference = Math.abs(
        recommendedMonthlyPayment - initialPreviousMonthlyPayment
      );

      // Only show recommendation if:
      // 1. The current difference is more than $0.07 (7 cents)
      // 2. The recommended option is better than current
      // 3. The recommended duration is different from current
      if (
        monthlyPaymentDifference > 7 &&
        recommendedDifference < monthlyPaymentDifference &&
        bestPossibleDurationCount !== currentInstallments
      ) {
        const initialPreviousMonthlyText = parsePrice(
          centsToDollars(initialPreviousMonthlyPayment)
        );

        recommendationInfo = `The number of months has been automatically adjusted to ${bestPossibleDurationCount} to keep the monthly payment close to ${initialPreviousMonthlyText}.`;
        return {
          monthlyPaymentInfo,
          recommendationInfo,
          adjustedInstallments: bestPossibleDurationCount,
        };
      }
    }

    return {
      monthlyPaymentInfo,
      recommendationInfo,
      adjustedInstallments: null,
    };
  };

  useEffect(() => {
    const validateFields = async () => {
      let isValidFlatAmount = false;
      let isValidNextInstallmentDate = false;
      let isValidRemainingAmount = false;
      const isValidRemainingInstallments =
        remainingInstallmentsField !== null &&
        remainingInstallmentsField !== undefined;
      const isPercentageType = minDownPaymentType === "Percentage";
      const isFlatValid = await trigger("paymentPlanMinDownPaymentFlat");
      const isPercentageValid = await trigger(
        "paymentPlanMinDownPaymentPercent"
      );
      if (isPercentageType) {
        isValidFlatAmount = !!isPercentageValid;
      } else {
        isValidFlatAmount = !!isFlatValid;
      }
      if (!showNextInstallmentDate) {
        isValidNextInstallmentDate = true;
      } else {
        const validNextInstallmentDate = validateNextInstallmentDate(
          nextInstallmentDateField,
          "future"
        );
        isValidNextInstallmentDate =
          typeof validNextInstallmentDate !== "string";
      }
      if (orderStatus === orderStatusConstants.SENT_TO_PATIENT) {
        isValidRemainingAmount = true;
      } else {
        isValidRemainingAmount =
          remainingAmountField !== null && remainingAmountField !== undefined;
      }

      let isValidInstallmentAmount = true;
      let isValidInstallmentFrequency = true;
      if (selectedPaymentOption === "setAmount") {
        const installmentAmountValid = await trigger("installmentAmount");
        const installmentFrequencyValid = await trigger("installmentFrequency");
        isValidInstallmentAmount = !!installmentAmountValid;
        isValidInstallmentFrequency = !!installmentFrequencyValid;
      }

      setPaymentPlanFieldsValid(
        isValidFlatAmount &&
          isValidNextInstallmentDate &&
          isValidRemainingAmount &&
          (selectedPaymentOption === "setAmount"
            ? isValidInstallmentAmount && isValidInstallmentFrequency
            : isValidRemainingInstallments)
      );
    };
    validateFields();
  }, [
    minDownPaymenFlat,
    minDownPaymentPercent,
    trigger,
    minDownPaymentType,
    nextInstallmentDateField,
    remainingInstallmentsField,
    remainingAmountField,
    totalPatientPortion,
    selectedPaymentOption,
    installmentAmountField,
    installmentFrequencyField,
  ]);

  // Re-validate installment amount when min/max limits change (e.g. remaining amount or down payment changed)
  useEffect(() => {
    if (selectedPaymentOption === "setAmount") {
      trigger("installmentAmount");
    }
  }, [
    selectedPaymentOption,
    maxInstallmentAmount,
    minInstallmentAmount,
    trigger,
  ]);

  // Monthly payment calculation for Patient Responsibility orders
  const monthlyPaymentInfo = useMemo(() => {
    const isSetAmountWithInstallment =
      paymentInfoOptions?.selectedPaymentOption === "setAmount" &&
      paymentInfoOptions?.currentInstallmentAmountInCents != null &&
      paymentInfoOptions.currentInstallmentAmountInCents > 0;
    const hasValidDuration =
      remainingInstallmentsField != null && remainingInstallmentsField > 0;
    if (
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY ||
      !editing ||
      orderStatus !== orderStatusConstants.PARTIALLY_PAID
    ) {
      return null;
    }
    if (!isSetAmountWithInstallment && !hasValidDuration) {
      return null;
    }

    const currentRemainingAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const initialRemainingAmount = paymentPlan?.remainingAmountInCents || 0;
    const hasChanges = currentRemainingAmount !== initialRemainingAmount;

    const { monthlyPaymentInfo } = calculatePaymentInfo(
      currentRemainingAmount,
      initialRemainingAmount,
      remainingInstallmentsField ?? 0,
      hasChanges,
      initialPreviousMonthlyPaymentInCents,
      paymentInfoOptions
    );

    return monthlyPaymentInfo;
  }, [
    orderType,
    editing,
    remainingInstallmentsField,
    remainingAmountField,
    paymentPlan?.remainingAmountInCents,
    initialRemainingInstallmentsCount,
    initialPreviousMonthlyPaymentInCents,
    paymentInfoOptions,
  ]);

  const onNextInstallmentDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const newValue = e.target.value;
    let currentValue = "";
    if (paymentPlan?.installments?.length) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset today's time to midnight
      const sortedInstallments = [...paymentPlan.installments]
        .filter((installment) => {
          const installmentDate = new Date(installment.dateDue);
          installmentDate.setHours(0, 0, 0, 0); // Reset installment's time to midnight
          return installmentDate >= today; // Compare only the date parts
        })
        .sort(
          (a, b) =>
            new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
        ); // Sort by dateDue

      // first installment with a null paymentId
      const firstNullPaymentIdInstallment = sortedInstallments.find(
        (installment) => !installment.paymentId
      );
      currentValue = firstNullPaymentIdInstallment?.dateDue
        ? parseDate(firstNullPaymentIdInstallment.dateDue, false, true)
        : "-";
    }

    setValue("nextInstallmentDateChanged", newValue !== currentValue);
    handleChangeDateWithMask(e, onChange);
  };

  // Show only persistent recommendation after auto-adjustment has happened
  // (Don't fall back to calculated one, or message won't hide when user edits)
  const displayRecommendationInfo = persistentRecommendation;

  // Recommendation calculation for Patient Responsibility orders
  const recommendationData = useMemo(() => {
    if (
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY ||
      !editing ||
      !remainingInstallmentsField ||
      remainingInstallmentsField <= 0 ||
      !initialRemainingInstallmentsCount ||
      initialRemainingInstallmentsCount <= 0 ||
      orderStatus !== orderStatusConstants.PARTIALLY_PAID
    ) {
      return { recommendationInfo: null, adjustedInstallments: null };
    }

    const currentRemainingAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const initialRemainingAmount = paymentPlan?.remainingAmountInCents || 0;
    const hasChanges = currentRemainingAmount !== initialRemainingAmount;

    if (!hasChanges) {
      return { recommendationInfo: null, adjustedInstallments: null };
    }

    const { recommendationInfo, adjustedInstallments } = calculatePaymentInfo(
      currentRemainingAmount,
      initialRemainingAmount,
      remainingInstallmentsField ?? 0,
      hasChanges,
      initialPreviousMonthlyPaymentInCents,
      paymentInfoOptions
    );

    return { recommendationInfo, adjustedInstallments };
  }, [
    orderType,
    editing,
    remainingInstallmentsField,
    remainingAmountField,
    paymentPlan?.remainingAmountInCents,
    initialRemainingInstallmentsCount,
    initialPreviousMonthlyPaymentInCents,
    paymentInfoOptions,
    orderStatus,
  ]);

  const { recommendationInfo, adjustedInstallments } = recommendationData;

  const isPaymentPlanActivated = useMemo(() => {
    return paymentPlan?.installments?.length;
  }, [paymentPlan?.installments?.length]);

  const remainingPaymentsFieldsLogicActive = useMemo(() => {
    return (
      !isPaymentPlanActivated &&
      [
        orderStatusConstants.SENT_TO_PATIENT,
        orderStatusConstants.NO_BALANCE_DUE,
      ].includes(orderStatus)
    );
  }, [isPaymentPlanActivated, orderStatus]);

  const numberOfPaymentsLabel = useMemo(() => {
    if (rearrangeFieldsOrder) {
      return "Number of payments (downpayment included)";
    }
    return remainingPaymentsFieldsLogicActive
      ? "Number of payments (downpayment included)"
      : "Remaining Installments";
  }, [rearrangeFieldsOrder, remainingPaymentsFieldsLogicActive]);

  // When in Set Amount mode, derive paymentPlanDuration and paymentPlanDurationFull from payment amount.
  // Same logic as duration input: when remainingPaymentsFieldsLogicActive, Full = duration + 1 (downpayment included); otherwise Full = duration. When order is PAID, Full = duration (per PaymentDetails resetForm).
  useEffect(() => {
    if (selectedPaymentOption !== "setAmount") return;
    const amount = installmentAmountField;
    if (amount == null || amount <= 0 || remainingAmountInCents <= 0) return;
    const installmentAmountInCents = dollarsToCents(amount);
    // CLE-3081: same fold rule and cap as the API, so the previewed count matches the installments it creates.
    const duration = Math.max(
      1,
      setAmountInstallmentCount(
        remainingAmountInCents,
        installmentAmountInCents
      )
    );
    const durationFull = remainingPaymentsFieldsLogicActive
      ? duration + 1
      : duration;

    setValue("paymentPlanDuration", duration);
    setValue("paymentPlanDurationFull", durationFull);
  }, [
    selectedPaymentOption,
    installmentAmountField,
    remainingAmountInCents,
    remainingPaymentsFieldsLogicActive,
    setValue,
  ]);

  // Track the previous payment option to detect switches
  const [prevPaymentOption, setPrevPaymentOption] = useState(
    selectedPaymentOption
  );

  // Flag so the "hide message on manual change" useEffect skips changes from our auto-adjustment
  const isAutoAdjustingRef = useRef(false);

  // When switching to "paymentPlan", auto-adjust duration to keep monthly payment
  // closest to the initial monthly payment.
  useEffect(() => {
    if (selectedPaymentOption === prevPaymentOption) return;
    setPrevPaymentOption(selectedPaymentOption);

    if (selectedPaymentOption !== "paymentPlan") return;
    if (!editing || !initialPreviousMonthlyPaymentInCents) return;
    if (remainingAmountInCents <= 0) return;

    const idealMonths =
      remainingAmountInCents / initialPreviousMonthlyPaymentInCents;
    let bestDuration = Math.max(1, Math.round(idealMonths));

    const maxAllowed = initialPreviousMonthlyPaymentInCents * 1.06;
    if (remainingAmountInCents / bestDuration > maxAllowed) {
      bestDuration += 1;
    }
    bestDuration = Math.min(bestDuration, MAX_PAYMENT_PLAN_DURATION);

    if (bestDuration === remainingInstallmentsField) return;

    const durationFull = remainingPaymentsFieldsLogicActive
      ? bestDuration + 1
      : bestDuration;

    isAutoAdjustingRef.current = true;
    setPrevInstallments(bestDuration);
    setLastAdjustedAmount(remainingAmountInCents);
    setValue("paymentPlanDuration", bestDuration);
    setValue("paymentPlanDurationFull", durationFull);

    const recommendedMonthlyText = parsePrice(
      centsToDollars(initialPreviousMonthlyPaymentInCents)
    );
    setPersistentRecommendation(
      `The number of months has been automatically adjusted to ${bestDuration} to keep the monthly payment close to ${recommendedMonthlyText}.`
    );
  }, [
    selectedPaymentOption,
    prevPaymentOption,
    editing,
    initialPreviousMonthlyPaymentInCents,
    remainingAmountInCents,
    remainingPaymentsFieldsLogicActive,
    remainingInstallmentsField,
    setValue,
  ]);

  const onNumberOfPaymentsChange = (e: InputNumberValueChangeParams) => {
    const newValue = e.value;
    if (newValue) {
      // Only set the type if it's not already "Flat amount" to avoid triggering
      // the useEffect in PaymentDetails that resets the flat value
      if (minDownPaymentType !== "Flat amount") {
        setValue("paymentPlanMinDownPaymentType", "Flat amount");
        setValue("paymentPlanMinDownPaymentPercent", null);
      }
      const downPaymentValue = centsToDollars(
        (totalPatientPortion || 0) / newValue
      );
      setValue("paymentPlanMinDownPaymentFlat", downPaymentValue);
    }
  };

  const getMinDownPaymentMessage = (value: number) => {
    const remainingAmount = centsToDollars(totalPatientPortion || 0) - value;
    const monthlyPayment = remainingInstallmentsField
      ? Math.floor((remainingAmount / remainingInstallmentsField) * 100) / 100
      : 0;
    return Math.abs(monthlyPayment - value) < 0.1
      ? `Same as monthly payment (default)`
      : "Custom amount";
  };

  // Auto-update the Remaining Installments field when remaining amount changes
  useEffect(() => {
    const currentAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const amountChanged = currentAmount !== lastAdjustedAmount;

    if (
      recommendationInfo &&
      adjustedInstallments &&
      editing &&
      amountChanged
    ) {
      // Save the recommendation message to persist it
      setPersistentRecommendation(recommendationInfo);

      // Use the adjusted installments number directly
      if (adjustedInstallments !== remainingInstallmentsField) {
        isAutoAdjustingRef.current = true;
        setLastAdjustedAmount(currentAmount);
        setPrevInstallments(adjustedInstallments);
        setValue("paymentPlanDuration", adjustedInstallments);
        setValue("paymentPlanDurationFull", adjustedInstallments);
      }
    }
  }, [
    recommendationInfo,
    adjustedInstallments,
    editing,
    remainingAmountField,
    setValue,
    lastAdjustedAmount,
  ]);

  // Hide message when user manually changes installments
  useEffect(() => {
    if (
      prevInstallments !== null &&
      remainingInstallmentsField !== prevInstallments
    ) {
      // Skip if this change came from our own auto-adjustment (option switch or amount change)
      if (isAutoAdjustingRef.current) {
        isAutoAdjustingRef.current = false;
        setPrevInstallments(remainingInstallmentsField);
        return;
      }
      // Check if this is from our auto-adjustment by comparing with adjustedInstallments
      // If the field matches adjustedInstallments, it's our auto-update, not a manual change
      if (
        adjustedInstallments &&
        remainingInstallmentsField === adjustedInstallments
      ) {
        // This is our auto-adjustment, just update prevInstallments
        setPrevInstallments(remainingInstallmentsField);
      } else {
        // User manually changed installments - hide the message
        setPersistentRecommendation(null);
        setPrevInstallments(remainingInstallmentsField);
      }
    }
  }, [remainingInstallmentsField, prevInstallments, adjustedInstallments]);

  // Reset persistent recommendation and flags when editing is toggled off
  useEffect(() => {
    if (!editing) {
      setPersistentRecommendation(null);
      setLastAdjustedAmount(null);
      setPrevInstallments(remainingInstallmentsField);
    }
  }, [editing, remainingInstallmentsField]);

  return (
    <div className="flex flex-column gap-2">
      <div className="formgrid grid w-100">
        {editable && selectedPaymentOption === "setAmount" && (
          <>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3">
              {editing ? (
                <Label
                  htmlFor="installmentAmount"
                  data-testid="installmentAmountLabel"
                >
                  Payment Amount
                </Label>
              ) : (
                <ReadOnlyLabel>Payment Amount</ReadOnlyLabel>
              )}
              <Controller
                name="installmentAmount"
                control={control}
                rules={{
                  required:
                    selectedPaymentOption === "setAmount"
                      ? validationConstants.PAYMENT_PLAN
                          .INSTALLMENT_AMOUNT_REQUIRED
                      : false,
                  validate: {
                    maxAmount: (value: number | null | undefined) => {
                      if (selectedPaymentOption !== "setAmount" || !value)
                        return true;
                      if (
                        value >
                        Math.round(maxInstallmentAmount * 100) / 100
                      ) {
                        return `Installment amount cannot exceed ${parsePrice(
                          maxInstallmentAmount
                        )}`;
                      }
                      return true;
                    },
                    minAmount: (value: number | null | undefined) => {
                      if (
                        selectedPaymentOption !== "setAmount" ||
                        !value ||
                        minInstallmentAmount <= 0
                      )
                        return true;
                      if (value < minInstallmentAmount - 0.01) {
                        return `Installment amount must be at least ${parsePrice(
                          minInstallmentAmount
                        )} to keep the payment plan within ${MAX_PAYMENT_PLAN_DURATION} payments`;
                      }
                      return true;
                    },
                  },
                }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <>
                    {!editing && (
                      <ReadOnlyValue>
                        {value != null ? parsePrice(value) : "—"}
                      </ReadOnlyValue>
                    )}
                    <StyledInputNumber
                      value={value}
                      currency="USD"
                      locale="en-US"
                      mode="currency"
                      disabled={!editing}
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "installmentAmount",
                          trigger,
                        })
                      }
                      inputId="installmentAmount"
                      data-testid="installmentAmount_PaymentPlan"
                      onValueChange={onChange}
                      min={1}
                      placeholder="Enter amount"
                      className={editing ? undefined : "hidden"}
                    />
                    {editing &&
                      (maxInstallmentAmount > 0 ||
                        minInstallmentAmount > 0) && (
                        <p className="text-xs mt-1 text-gray-600 font-normal">
                          {minInstallmentAmount > 0 &&
                            `Min: ${parsePrice(minInstallmentAmount)}`}
                          {minInstallmentAmount > 0 &&
                            maxInstallmentAmount > 0 &&
                            " | "}
                          {maxInstallmentAmount > 0 &&
                            `Max: ${parsePrice(maxInstallmentAmount)}`}
                        </p>
                      )}
                    {getFormErrorMessage("installmentAmount", errors)}
                  </>
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3">
              {editing ? (
                <Label
                  htmlFor="installmentFrequency"
                  data-testid="installmentFrequencyLabel"
                >
                  Frequency
                </Label>
              ) : (
                <ReadOnlyLabel>Frequency</ReadOnlyLabel>
              )}
              <Controller
                name="installmentFrequency"
                control={control}
                rules={{
                  validate: (value) =>
                    selectedPaymentOption === "setAmount" && !value
                      ? "Please select a payment frequency"
                      : true,
                }}
                render={({ field: { onChange, value } }) =>
                  editing ? (
                    <>
                      <Dropdown
                        id="installmentFrequency"
                        data-testid="installmentFrequency_PaymentPlan"
                        value={value}
                        onChange={onChange}
                        disabled={!editing}
                        options={INSTALLMENT_FREQUENCY_OPTIONS}
                        placeholder="Select frequency"
                        className="w-full"
                      />
                      {getFormErrorMessage("installmentFrequency", errors)}
                    </>
                  ) : (
                    <ReadOnlyValue>
                      {INSTALLMENT_FREQUENCY_OPTIONS.find(
                        (o) => o.value === value
                      )?.label ??
                        value ??
                        "—"}
                    </ReadOnlyValue>
                  )
                }
              />
            </div>
          </>
        )}
        {editable && selectedPaymentOption !== "setAmount" && (
          <div
            className={`field flex flex-column col-12  ${
              remainingPaymentsFieldsLogicActive
                ? "md:col-8 lg:col-6 xl:col-5 xxl:col-3"
                : "md:col-4 lg:col-3"
            }`}
          >
            {editing ? (
              <Label htmlFor="paymentPlanDurationFull">
                {numberOfPaymentsLabel}
              </Label>
            ) : (
              <ReadOnlyLabel>{numberOfPaymentsLabel}</ReadOnlyLabel>
            )}
            <Controller
              data-testid="paymentPlanDurationFull"
              name="paymentPlanDurationFull"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  {!editing && (
                    <ReadOnlyValue>
                      {value != null ? String(value) : "—"}
                    </ReadOnlyValue>
                  )}
                  <StyledInputNumber
                    disabled={!editing}
                    id="paymentPlanDurationFull"
                    aria-labelledby="paymentPlanDurationFull"
                    value={value}
                    onValueChange={(e) => {
                      if (e.value === null || e.value === undefined) {
                        setValue("paymentPlanDuration", null);
                      }
                      if (e.value === 0) {
                        setValue("paymentPlanDuration", 0);
                      }
                      if (e.value) {
                        setValue(
                          "paymentPlanDuration",
                          !remainingPaymentsFieldsLogicActive
                            ? e.value
                            : e.value - 1
                        );
                      }
                      onChange(e.value);
                      if (
                        rearrangeFieldsOrder &&
                        remainingPaymentsFieldsLogicActive
                      ) {
                        onNumberOfPaymentsChange(e);
                      }
                    }}
                    onChange={(e) =>
                      e.value !== null &&
                      e.value !== undefined &&
                      remainingPaymentsFieldsLogicActive &&
                      setShowMinPaymentWarning(e.value < 2)
                    }
                    min={!remainingPaymentsFieldsLogicActive ? 0 : 2}
                    max={!remainingPaymentsFieldsLogicActive ? 50 : 51}
                    className={editing ? undefined : "hidden"}
                  />
                  {showMinPaymentWarning &&
                    remainingPaymentsFieldsLogicActive && (
                      <p className="text-sm mt-1 text-orange-500">
                        Minimum: 2 payments (today + next installment)
                      </p>
                    )}
                </>
              )}
            />
          </div>
        )}
        {showMinDownPaymentFields && (
          <div className="field flex flex-column col-12 sm:col-3 md:col-4 lg:col-3 xl:col-1">
            {editing ? (
              <Label
                htmlFor="paymentPlanMinDownPaymentType"
                data-testid="paymentPlanMinDownPaymentType"
              >
                Type
              </Label>
            ) : (
              <ReadOnlyLabel>Type</ReadOnlyLabel>
            )}
            <Controller
              name="paymentPlanMinDownPaymentType"
              control={control}
              render={({ field: { onChange, value } }) =>
                editing ? (
                  <Dropdown
                    id="paymentPlanMinDownPaymentType"
                    data-testid="minDownPaymentType_PaymentPlan"
                    value={value}
                    disabled={!minDownPaymentEnabled}
                    onChange={onChange}
                    options={MIN_DOWN_PAYMENT_TYPE_OPTIONS}
                    placeholder="Select minimum down payment type"
                    className="w-full"
                  />
                ) : (
                  <ReadOnlyValue>
                    {MIN_DOWN_PAYMENT_TYPE_OPTIONS.find(
                      (o) => o.value === value
                    )?.label ??
                      value ??
                      "—"}
                  </ReadOnlyValue>
                )
              }
            />
            {minDownPaymentType === "Percentage" &&
              rearrangeFieldsOrder &&
              minDownPaymentPercent && (
                <span className="text-sm mt-1 text-gray-600">
                  ={" "}
                  {parsePrice(
                    (centsToDollars(totalPatientPortion || 0) *
                      minDownPaymentPercent) /
                      100
                  )}
                </span>
              )}
          </div>
        )}
        {showPercentageField && showMinDownPaymentFields && (
          <div className="field flex flex-column col-12 sm:col-9 md:col-4 lg:col-3">
            {editing ? (
              <Label htmlFor="paymentPlanMinDownPaymentPercent">
                First Payment Amount
              </Label>
            ) : (
              <ReadOnlyLabel>First Payment Amount</ReadOnlyLabel>
            )}
            <Controller
              data-testid="paymentPlanMinDownPaymentPercent"
              name="paymentPlanMinDownPaymentPercent"
              control={control}
              rules={{
                required:
                  validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_PERCENT,
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                <>
                  {!editing && (
                    <ReadOnlyValue>
                      {value != null ? `${value}%` : "—"}
                    </ReadOnlyValue>
                  )}
                  <StyledInputNumber
                    disabled={!minDownPaymentEnabled}
                    value={value}
                    min={5}
                    onValueChange={onChange}
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentPercent",
                        trigger,
                      })
                    }
                    suffix="%"
                    inputId="paymentPlanMinDownPaymentPercent"
                    data-testid="paymentPlanMinDownPaymentPercent"
                    className={editing ? undefined : "hidden"}
                  />
                  {getFormErrorMessage(
                    "paymentPlanMinDownPaymentPercent",
                    errors
                  )}
                </>
              )}
            />
          </div>
        )}
        {showFlatAmountField && showMinDownPaymentFields && (
          <div className="field flex flex-column col-12 sm:col-9 md:col-4 lg:col-3">
            {editing ? (
              <Label
                htmlFor="paymentPlanMinDownPaymentFlat"
                data-testid="paymentPlanMinDownPaymentFlat"
              >
                First Payment Amount
              </Label>
            ) : (
              <ReadOnlyLabel>First Payment Amount</ReadOnlyLabel>
            )}

            <Controller
              name="paymentPlanMinDownPaymentFlat"
              control={control}
              rules={{
                required:
                  validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_FLAT,
                validate: validateMinDownPaymentFlatWithState,
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  {!editing && (
                    <ReadOnlyValue>
                      {minDownPaymentFlatDisplayValue != null
                        ? parsePrice(minDownPaymentFlatDisplayValue)
                        : "—"}
                    </ReadOnlyValue>
                  )}
                  <StyledInputNumber
                    value={minDownPaymentFlatDisplayValue}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    disabled={!minDownPaymentEnabled}
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentFlat",
                        trigger,
                      })
                    }
                    inputId="paymentPlanMinDownPaymentFlat"
                    data-testid="minDownPaymentFlat_PaymentPlan"
                    onValueChange={(e) => {
                      // When user changes value, calculate total with fees for storage
                      const newValue = e.value;
                      const valueToStore =
                        newValue &&
                        order?.clearPatientFeeValueInPercentage &&
                        orderType === orderTypeConstants.GFE
                          ? addClearPatientFee(
                              newValue,
                              order.clearPatientFeeValueInPercentage
                            )
                          : newValue;
                      onChange(valueToStore);
                    }}
                    min={10}
                    aria-autocomplete="none"
                    className={editing ? undefined : "hidden"}
                  />
                  <div>
                    {value &&
                      order?.clearPatientFeeValueInPercentage &&
                      orderType === orderTypeConstants.GFE && (
                        <span className="text-sm mt-1 text-primary">
                          with fees: {parsePrice(value)}
                        </span>
                      )}{" "}
                    {value !== null &&
                      value !== undefined &&
                      rearrangeFieldsOrder && (
                        <span className="text-xs mts-1 text-gray-600">
                          {getMinDownPaymentMessage(value)}
                        </span>
                      )}
                  </div>
                  {getFormErrorMessage("paymentPlanMinDownPaymentFlat", errors)}
                </>
              )}
            />
          </div>
        )}
        {showRemainingAmountField && (
          <div
            className="field flex flex-column col-12 md:col-4 lg:col-3"
            style={{
              order:
                orderType === orderTypeConstants.PATIENT_RESPONSIBILITY &&
                orderStatus === orderStatusConstants.SENT_TO_PATIENT
                  ? 2
                  : undefined,
            }}
          >
            {editing ? (
              <Label htmlFor="paymentPlanRemainingAmount">
                Remaining Amount
              </Label>
            ) : (
              <ReadOnlyLabel>Remaining Amount</ReadOnlyLabel>
            )}
            <Controller
              data-testid="paymentPlanRemainingAmount"
              name="paymentPlanRemainingAmount"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  {!editing && (
                    <ReadOnlyValue>
                      {value != null ? parsePrice(value) : "-"}
                    </ReadOnlyValue>
                  )}
                  {remainingAmountDisabled && !value ? (
                    <InputText
                      value="-"
                      disabled
                      className={editing ? undefined : "hidden"}
                    />
                  ) : (
                    <StyledInputNumber
                      disabled={remainingAmountDisabled}
                      value={value}
                      currency="USD"
                      locale="en-US"
                      mode="currency"
                      inputId="paymentPlanRemainingAmount"
                      data-testid="paymentPlanRemainingAmount"
                      onValueChange={onChange}
                      min={0}
                      className={editing ? undefined : "hidden"}
                    />
                  )}
                </>
              )}
            />
          </div>
        )}
        {showNextInstallmentDate && (
          <div className="field flex flex-column col-12 md:col-4 lg:col-3">
            {editing ? (
              <Label htmlFor="nextInstallmentDate">Next Installment Date</Label>
            ) : (
              <ReadOnlyLabel>Next Installment Date</ReadOnlyLabel>
            )}
            <Controller
              data-testid="nextInstallmentDate"
              name="nextInstallmentDate"
              control={control}
              rules={{
                validate: (date) => validateNextInstallmentDate(date, "future"),
                required:
                  validationConstants.PAYMENT_PLAN.NEXT_INSTALLMENT_DATE,
              }}
              render={({ field: { onChange, onBlur, value } }) =>
                editing ? (
                  <>
                    <InputText
                      disabled={!editing}
                      id="nextInstallmentDate"
                      value={value || ""}
                      placeholder="99/99/9999"
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "nextInstallmentDate",
                          trigger,
                        })
                      }
                      onChange={(e) => onNextInstallmentDateChange(e, onChange)}
                    />

                    {getFormErrorMessage("nextInstallmentDate", errors)}
                  </>
                ) : (
                  <ReadOnlyValue>{value || "—"}</ReadOnlyValue>
                )
              }
            />
          </div>
        )}
        {orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY &&
          userInfo?.scope === UserRole.SUPER_ADMIN && (
            <div className="field flex-column col-12 md:col-8 lg:col-6 xl:col-3">
              {editing ? (
                <Label
                  htmlFor="providerDistributionLogic"
                  data-testid="providerDistributionLogic"
                >
                  Provider Distribution Logic
                </Label>
              ) : (
                <ReadOnlyLabel>Provider Distribution Logic</ReadOnlyLabel>
              )}
              <Controller
                name="providerDistributionLogic"
                control={control}
                render={({ field: { onChange, value } }) =>
                  editing ? (
                    <Dropdown
                      id="providerDistributionLogic"
                      data-testid="providerDistributionLogic_PaymentPlan"
                      value={value}
                      onChange={onChange}
                      disabled={!editing}
                      options={Object.values(distributionLogicOptions)}
                      placeholder="Select provider distribution logic"
                      className="w-full"
                    />
                  ) : (
                    <ReadOnlyValue>
                      {Object.values(distributionLogicOptions).find(
                        (o) => o.value === value
                      )?.label ??
                        value ??
                        "—"}
                    </ReadOnlyValue>
                  )
                }
              />
            </div>
          )}
        {showPaymentPlanEnableButton && editing && (
          <div className="field flex flex-column col-12 md:col-4 lg:col-3 xxl:col-2">
            <Label
              htmlFor="paymentPlanEnabled"
              data-testid="paymentPlanEnabled"
            >
              Payment Plan Enabled
            </Label>
            <Controller
              name="paymentPlanEnabled"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSwitch
                  checked={value}
                  onChange={onChange}
                  disabled={!editing}
                />
              )}
            />
          </div>
        )}
        {showRemainingAmountsSection && (
          <RemainingAmountEditTable
            key={`${order?.updatedAt}-${order?.id}`}
            remainingAmountBreakdown={remainingAmountBreakdown}
            differenceByProvider={differenceByProvider}
            setDifferenceByProvider={setDifferenceByProvider}
            setValuesByProvider={setValuesByProvider}
            valuesByProvider={valuesByProvider}
            clearFeePercentage={Number(
              paymentPlanBreakDown?.clearFee?.valueInPercentage
            )}
            clearPatientFeePercentage={order?.clearPatientFeeValueInPercentage}
            isGFE={orderType === orderTypeConstants.GFE}
            initialPaymentPlanRemainingAmount={
              paymentPlan?.remainingAmountInCents || 0
            }
            initialPreviousMonthlyPaymentInCents={
              initialPreviousMonthlyPaymentInCents
            }
            paymentInfoOptions={paymentInfoOptions}
            parentRecommendation={persistentRecommendation}
            remainingInstallments={remainingInstallmentsField}
            calculatePaymentInfo={calculatePaymentInfo}
            accurateRemainingTotal={accurateRemainingTotal}
            selectedPaymentOption={selectedPaymentOption}
          />
        )}
      </div>
      <div>
        {monthlyPaymentInfo && (
          <>
            <div className="flex w-full bg-gray-100 border-t-1 border-gray-300 py-3 gap-4">
              <div className="w-9 flex align-items-center justify-content-end">
                <p className="font-bold">Remaining amount:</p>
              </div>
              <div className="w-3">
                <p className="font-bold">
                  {parsePrice(remainingAmountField || 0)}{" "}
                  {isPaymentPlanRemainingAmountChanged &&
                    `(previously ${parsePrice(
                      centsToDollars(paymentPlan?.remainingAmountInCents || 0)
                    )})`}
                </p>
              </div>
            </div>
            <div className="flex w-full bg-gray-100 border-y-1 border-gray-300 py-3 gap-4">
              <div className="w-9 flex align-items-center justify-content-end">
                <p className="font-bold">Payment Amount:</p>
              </div>
              <div className="w-3">
                <p className="font-bold">{monthlyPaymentInfo}</p>
              </div>
            </div>
          </>
        )}
        {displayRecommendationInfo &&
          !showRemainingAmountsSection &&
          selectedPaymentOption !== "setAmount" &&
          orderType === orderTypeConstants.PATIENT_RESPONSIBILITY &&
          orderStatus === orderStatusConstants.PARTIALLY_PAID && (
            <div className="flex w-full bg-gray-100 border-bottom-1 border-gray-300 py-3 gap-4">
              <div className="w-9 flex align-items-center justify-content-end gap-2">
                <i className="pi pi-exclamation-triangle text-orange-500 text-base" />
                <p className="text-base font-medium text-orange-500">
                  Attention:
                </p>
              </div>
              <div className="w-3">
                <p className="text-sm font-medium text-orange-700">
                  {displayRecommendationInfo}
                </p>
              </div>
            </div>
          )}
      </div>
      {[orderStatusConstants.SENT_TO_PATIENT].includes(orderStatus) &&
        editing && (
          <PaymentPlanDetails
            totalPatientPortion={totalPatientPortion}
            order={order}
            initialPayment={initialPayment}
            selectedPaymentOption={selectedPaymentOption}
          />
        )}
    </div>
  );
}
